import React, { useContext, useState } from "react";
import { useMutation } from "@tanstack/react-query";

//App
import OrganizationForm from "./OrganizationForm";
import organizationApi from "../../app/model/api/organizationApi";
import { OrganizationFormContainerProps } from "../../types/organization";
import { useOrganizationLookupsQuery } from "./hooks/useOrganization";
import { OrganizationDto } from "../../app/model/api/types/dto/organizationDto";
import { OrganizationViewModel } from "../../viewModels/organizationViewModel";

//Common
import { Snackbar } from "../common/VFX-UI";
import { SnackbarState } from "../../types";
import { apiErrorMessage } from "../../utility/lib/error";
import { EMPTY_GUID } from "../../utility/constants/constants";
import { OrganizationViewContext } from "./OrganizationContainer";
import { FileViewModel } from "../../viewModels/fileViewModel";

export const OrganizationFormContainer: React.FC<
  OrganizationFormContainerProps
> = (props) => {
  const { onClose, onShowSuccess } = props;
  const organizationContext = useContext(OrganizationViewContext);

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    showErrorSnackbar: false,
    errorMessage: "",
  });

  const successMessage = organizationContext?.id
    ? "Organization updated"
    : "New Organization added";

  const onShowErrorSnackbar = (errorMessage: string) => {
    setSnackbar({
      showErrorSnackbar: true,
      errorMessage,
    });
  };

  const onCloseSnackbar = () => {
    setSnackbar({
      showErrorSnackbar: false,
      errorMessage: "",
    });
  };

  const { status, mutate } = useMutation<void, Error, OrganizationDto>({
    mutationFn: async (payload: OrganizationDto) => {
      const response = await organizationApi.save(payload);
      const errorMessage = apiErrorMessage(response.errorMessages);
      if (errorMessage) {
        onShowErrorSnackbar(errorMessage);
      } else {
        onShowSuccess(successMessage);
      }
    },
    onError: () => console.log("error"),
    onSuccess: () => console.log("success"),
  });

  const handleOnSubmit = async (values: OrganizationViewModel) => {
    const organization: OrganizationDto = values.toDto();
    mutate(organization);
  };

  const organizationLookupQuery = useOrganizationLookupsQuery();

  const initialValues = organizationContext?.id
    ? new OrganizationViewModel(
        organizationContext?.data?.id ?? EMPTY_GUID,
        organizationContext?.data?.name ?? null,
        organizationContext?.data?.description ?? null,
        organizationContext?.data?.addressLine1 ?? null,
        organizationContext?.data?.addressLine2 ?? null,
        organizationContext?.data?.city ?? null,
        organizationContext.data?.stateId !== null &&
        organizationContext.data?.stateId !== undefined
          ? [organizationContext.data?.stateId]
          : null,
        organizationContext?.data?.zipCode ?? null,
        organizationContext?.data?.mainPhone ?? null,
        organizationContext?.data?.fax ?? null,
        organizationContext?.data?.websiteUrl ?? null,
        organizationContext?.data?.payrollDeductionFrequencyId ?? null,
        organizationContext?.data?.isActive ?? null,
        organizationContext?.data?.sortValue ?? null,
        organizationContext?.data?.logo
          ? FileViewModel.mapFromDto(organizationContext?.data?.logo)
          : null,
        organizationContext?.data?.contactName ?? null,
        organizationContext?.data?.contactPhone ?? null,
        organizationContext?.data?.contactEmail ?? null
      )
    : new OrganizationViewModel(
        EMPTY_GUID,
        "",
        "",
        "",
        "",
        "",
        null,
        "",
        "",
        "",
        null,
        null,
        true,
        null,
        null,
        null,
        null,
        null
      );

  return (
    <>
      <OrganizationForm
        initialVal={initialValues}
        status={status}
        lookups={organizationLookupQuery?.data?.result}
        handleOnSubmit={handleOnSubmit}
        onClose={onClose}
      />
      {snackbar.showErrorSnackbar && (
        <Snackbar
          open={true}
          message={snackbar.errorMessage}
          handleClose={onCloseSnackbar}
          severity="danger"
        />
      )}
    </>
  );
};
